import React, { useState } from "react";
import {
  Plus, Edit2, Trash2, Search, Shield, X, Check,
  ChevronDown, ChevronUp, Users, KeyRound,
} from "lucide-react";
import { useAdmin } from "../admin-context";
import { PERMISSION_GROUPS, ALL_PERMISSIONS, NO_PERMISSIONS } from "../../../utils/permissions";

/* ── Toggle switch ────────────────────────────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none
      ${checked ? "bg-orange-500" : "bg-gray-200"}`}
  >
    <span className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow ring-0 transition-transform duration-200
      ${checked ? "translate-x-4" : "translate-x-0"}`} />
  </button>
);

/* ── Permission matrix ────────────────────────────────────────────────────── */
const PermissionMatrix = ({ permissions, onChange }) => {
  const [expanded, setExpanded] = useState({});

  const toggleGroup = (g) => setExpanded((p) => ({ ...p, [g]: !p[g] }));

  const setGroupAll = (group, value) => {
    const updated = { ...permissions };
    group.permissions.forEach((p) => { updated[p.key] = value; });
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {PERMISSION_GROUPS.map((group) => {
        const open = expanded[group.group] !== false;
        const allOn = group.permissions.every((p) => permissions[p.key]);

        return (
          <div key={group.group} className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
              <button type="button" onClick={() => toggleGroup(group.group)}
                className="flex items-center gap-2 text-sm font-bold text-gray-700 hover:text-gray-900">
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {group.group}
              </button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">{allOn ? "All on" : "Custom"}</span>
                <Toggle checked={allOn} onChange={(val) => setGroupAll(group, val)} />
              </div>
            </div>
            {open && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 px-4 py-3">
                {group.permissions.map((perm) => (
                  <label key={perm.key} className="flex items-center justify-between gap-2 text-sm text-gray-600 cursor-pointer">
                    <span>{perm.label}</span>
                    <Toggle
                      checked={!!permissions[perm.key]}
                      onChange={(val) => onChange({ ...permissions, [perm.key]: val })}
                    />
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Permission summary badge ─────────────────────────────────────────────── */
const PermissionSummary = ({ permissions }) => {
  if (!permissions || !Object.keys(permissions).length)
    return <span className="text-xs text-gray-400">No permissions configured</span>;

  const total   = Object.keys(permissions).length;
  const enabled = Object.values(permissions).filter(Boolean).length;

  if (enabled === total)
    return <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full border border-green-200">Full Access</span>;
  if (enabled === 0)
    return <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">No Access</span>;
  return <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2 py-0.5 rounded-full border border-orange-200">{enabled}/{total} permissions</span>;
};

/* ── Role badge ───────────────────────────────────────────────────────────── */
const RoleBadge = ({ role }) => {
  if (!role) return <span className="text-xs text-gray-400 italic">No role</span>;
  return (
    <span className="text-xs font-bold text-orange-700 bg-orange-100 px-2.5 py-0.5 rounded-full border border-orange-200 flex items-center gap-1 w-fit">
      <Shield size={10} /> {role.name}
    </span>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════ */
/*                              MAIN PAGE                                     */
/* ═══════════════════════════════════════════════════════════════════════════ */
const UserRoles = () => {
  const {
    roles, addRole, updateRole, deleteRole, hasPermission,
    users, addUser, updateUser, deleteUser,
  } = useAdmin();

  const [tab, setTab] = useState("roles"); // "roles" | "users"
  const [search, setSearch] = useState("");

  // ── Roles modal state ──────────────────────────────────────────
  const [roleModal, setRoleModal] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", permissions: { ...NO_PERMISSIONS } });
  const [deleteRoleId, setDeleteRoleId] = useState(null);

  // ── Users modal state ──────────────────────────────────────────
  const [userModal, setUserModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [userForm, setUserForm] = useState({ name: "", email: "", phone: "", password: "", role_id: "", status: "active" });
  const [deleteUserId, setDeleteUserId] = useState(null);

  const canCreateRole = hasPermission("roles.create");
  const canEditRole   = hasPermission("roles.edit");
  const canDeleteRole = hasPermission("roles.delete");
  const canCreateUser = hasPermission("customers.create");
  const canEditUser   = hasPermission("customers.edit");
  const canDeleteUser = hasPermission("customers.delete");

  /* ── Role handlers ──────────────────────────────────────────── */
  const openRoleModal = (role = null) => {
    if (role) {
      setEditingRoleId(role.id);
      setRoleForm({ name: role.name, permissions: { ...NO_PERMISSIONS, ...(role.permissions || {}) } });
    } else {
      setEditingRoleId(null);
      setRoleForm({ name: "", permissions: { ...NO_PERMISSIONS } });
    }
    setRoleModal(true);
  };

  const submitRole = async (e) => {
    e.preventDefault();
    if (editingRoleId) await updateRole(editingRoleId, roleForm);
    else await addRole(roleForm);
    setRoleModal(false);
  };

  /* ── User handlers ──────────────────────────────────────────── */
  const openUserModal = (user = null) => {
    if (user) {
      setEditingUserId(user.id);
      setUserForm({ name: user.name || "", email: user.email || "", phone: user.phone || "", password: "", role_id: user.role_id || user.role?.id || "", status: user.status || "active" });
    } else {
      setEditingUserId(null);
      setUserForm({ name: "", email: "", phone: "", password: "", role_id: "", status: "active" });
    }
    setUserModal(true);
  };

  const submitUser = async (e) => {
    e.preventDefault();
    const payload = { ...userForm, role_id: userForm.role_id || null, license_id: 2 };
    if (!payload.password) delete payload.password;
    if (editingUserId) await updateUser(editingUserId, payload);
    else await addUser(payload);
    setUserModal(false);
  };

  /* ── Filter data ────────────────────────────────────────────── */
  const filteredRoles = roles.filter((r) => r.name?.toLowerCase().includes(search.toLowerCase()));
  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  /* ════════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Roles & Permissions</h1>
          <p className="text-gray-500 text-sm">Manage roles and assign them to users.</p>
        </div>
        {tab === "roles" && canCreateRole && (
          <button onClick={() => openRoleModal()}
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95">
            <Plus size={20} /> Add Role
          </button>
        )}
        {tab === "users" && canCreateUser && (
          <button onClick={() => openUserModal()}
            className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-orange-200 flex items-center gap-2 transition active:scale-95">
            <Plus size={20} /> Add User
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        <button onClick={() => { setTab("roles"); setSearch(""); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition
            ${tab === "roles" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
          <KeyRound size={15} /> Roles
        </button>
        <button onClick={() => { setTab("users"); setSearch(""); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition
            ${tab === "users" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
          <Users size={15} /> Users
        </button>
      </div>

      {/* Search */}
      <div className="relative w-full md:w-72">
        <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
        <input type="text" placeholder={tab === "roles" ? "Search roles…" : "Search users…"}
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition" />
      </div>

      {/* ── ROLES TAB ─────────────────────────────────────────────── */}
      {tab === "roles" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-100">
            {filteredRoles.length === 0 && (
              <div className="p-10 text-center text-gray-400">
                {roles.length === 0 ? "No roles yet. Create your first role." : "No roles match your search."}
              </div>
            )}
            {filteredRoles.map((role) => {
              const perms = role.permissions || {};
              const enabled = Object.values(perms).filter(Boolean).length;

              return (
                <div key={role.id} className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Shield size={20} className="text-orange-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900">{role.name}</p>
                    <div className="mt-1"><PermissionSummary permissions={perms} /></div>
                  </div>
                  <div className="hidden lg:flex flex-wrap gap-1 max-w-xs">
                    {PERMISSION_GROUPS.flatMap((g) =>
                      g.permissions.filter((p) => perms[p.key]).map((p) => (
                        <span key={p.key} className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Check size={10} /> {p.key.split(".")[1]}
                        </span>
                      ))
                    ).slice(0, 6)}
                    {enabled > 6 && <span className="text-xs text-gray-400">+{enabled - 6} more</span>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canEditRole && (
                      <button onClick={() => openRoleModal(role)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Edit role">
                        <Edit2 size={18} />
                      </button>
                    )}
                    {canDeleteRole && (
                      <button onClick={() => setDeleteRoleId(role.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete role">
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── USERS TAB ─────────────────────────────────────────────── */}
      {tab === "users" && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider">
                <tr>
                  <th className="p-5">Name</th>
                  <th className="p-5">Email</th>
                  <th className="p-5">Phone</th>
                  <th className="p-5">Role</th>
                  <th className="p-5">Status</th>
                  <th className="p-5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.length === 0 && (
                  <tr><td colSpan="6" className="p-8 text-center text-gray-400">No users found.</td></tr>
                )}
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition">
                    <td className="p-5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm flex-shrink-0">
                          {user.name?.[0]?.toUpperCase() || "?"}
                        </div>
                        <span className="font-bold text-gray-900">{user.name}</span>
                      </div>
                    </td>
                    <td className="p-5 text-gray-500 text-sm">{user.email}</td>
                    <td className="p-5 text-gray-500 text-sm">{user.phone || "—"}</td>
                    <td className="p-5"><RoleBadge role={user.role} /></td>
                    <td className="p-5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${user.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                        {user.status || "active"}
                      </span>
                    </td>
                    <td className="p-5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEditUser && (
                          <button onClick={() => openUserModal(user)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition" title="Edit user">
                            <Edit2 size={18} />
                          </button>
                        )}
                        {canDeleteUser && (
                          <button onClick={() => setDeleteUserId(user.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition" title="Delete user">
                            <Trash2 size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Create/Edit Role Modal ─────────────────────────────────── */}
      {roleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex justify-between items-center p-5 border-b bg-gray-50 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800">{editingRoleId ? "Edit Role" : "Create Role"}</h3>
              <button onClick={() => setRoleModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-full transition"><X size={20} /></button>
            </div>
            <form onSubmit={submitRole} className="flex flex-col flex-1 overflow-hidden">
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Role Name</label>
                  <input required type="text" value={roleForm.name}
                    onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition"
                    placeholder="e.g. Manager, Cashier, Staff…" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Permissions</label>
                  <div className="flex gap-2 mb-3">
                    <button type="button" onClick={() => setRoleForm({ ...roleForm, permissions: { ...ALL_PERMISSIONS } })}
                      className="text-xs px-3 py-1 rounded-lg bg-green-100 text-green-700 font-bold hover:bg-green-200 transition">Select All</button>
                    <button type="button" onClick={() => setRoleForm({ ...roleForm, permissions: { ...NO_PERMISSIONS } })}
                      className="text-xs px-3 py-1 rounded-lg bg-gray-100 text-gray-600 font-bold hover:bg-gray-200 transition">Clear All</button>
                  </div>
                  <PermissionMatrix permissions={roleForm.permissions}
                    onChange={(perms) => setRoleForm({ ...roleForm, permissions: perms })} />
                </div>
              </div>
              <div className="flex gap-3 p-5 border-t flex-shrink-0">
                <button type="button" onClick={() => setRoleModal(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
                <button type="submit"
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 shadow-md transition">
                  {editingRoleId ? "Save Changes" : "Create Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Create/Edit User Modal ─────────────────────────────────── */}
      {userModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800">{editingUserId ? "Edit User" : "Add User"}</h3>
              <button onClick={() => setUserModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-full transition"><X size={20} /></button>
            </div>
            <form onSubmit={submitUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
                <input required type="text" value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition"
                  placeholder="e.g. John Doe" />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Email</label>
                <input required type="email" value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition"
                  placeholder="e.g. john@example.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Phone</label>
                  <input type="text" value={userForm.phone}
                    onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition"
                    placeholder="+1 555 000" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">
                    {editingUserId ? "New Password" : "Password"}
                  </label>
                  <input type="password" value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    required={!editingUserId}
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition"
                    placeholder={editingUserId ? "Leave blank to keep" : "Required"} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Role</label>
                  <select value={userForm.role_id}
                    onChange={(e) => setUserForm({ ...userForm, role_id: e.target.value })}
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition bg-white">
                    <option value="">— No Role (Customer) —</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
                  <select value={userForm.status}
                    onChange={(e) => setUserForm({ ...userForm, status: e.target.value })}
                    className="w-full px-4 py-2 border rounded-xl outline-none focus:ring-2 focus:ring-orange-500 transition bg-white">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setUserModal(false)}
                  className="flex-1 py-3 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
                <button type="submit"
                  className="flex-1 py-3 bg-orange-500 text-white rounded-xl font-bold hover:bg-orange-600 shadow-md transition">
                  {editingUserId ? "Save Changes" : "Create User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Role Confirm ────────────────────────────────────── */}
      {deleteRoleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <Trash2 size={24} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Delete Role?</h3>
            <p className="text-sm text-gray-500">Users assigned to this role will lose access immediately.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setDeleteRoleId(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={async () => { await deleteRole(deleteRoleId); setDeleteRoleId(null); }}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete User Confirm ────────────────────────────────────── */}
      {deleteUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <Trash2 size={24} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Delete User?</h3>
            <p className="text-sm text-gray-500">This action cannot be undone.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setDeleteUserId(null)}
                className="flex-1 py-2.5 border border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition">Cancel</button>
              <button onClick={async () => { await deleteUser(deleteUserId); setDeleteUserId(null); }}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-bold hover:bg-red-600 transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRoles;
